import { Link } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardBody, Chip } from '@vnetwork/ui/primitives';
import { InfoGrid, type InfoItem } from '@/pages/account/components/InfoGrid';
import { formatDate, formatMoney } from '@/lib/format';
import type { ContractDetail } from '@/api/contracts';

/** Yes/No chip for boolean contract flags (auto-renew / SLA / NDA). */
function boolChip(on?: boolean) {
  return (
    <Chip appearance="subtle" tone={on ? 'success' : 'neutral'} size="sm">
      {on ? 'Yes' : 'No'}
    </Chip>
  );
}

/** Owner cell → link to the customer, "{id} - {company|name}" like legacy. */
function ownerValue(detail: ContractDetail) {
  const name = detail.company_name || detail.customer_name;
  if (!detail.customer_id) return name || undefined;
  return (
    <Link to={`/customers/${detail.customer_id}`} style={{ color: 'var(--vn-primary-text)' }}>
      {[detail.customer_id, name].filter(Boolean).join(' - ')}
    </Link>
  );
}

/** Render the contract's services block — single line links or an n-service list. */
function servicesItems(detail: ContractDetail): InfoItem[] {
  const services = detail.services ?? [];
  if (services.length <= 1) {
    const svc = services[0];
    const sid = svc?.service_id ?? detail.service_id;
    const label = svc ? [svc.service_id, svc.service_name].filter(Boolean).join(' - ') : detail.product_group_name;
    return [
      {
        label: 'Service',
        value: sid ? (
          <Link to={`/services/${sid}`} style={{ color: 'var(--vn-primary-text)' }}>
            {label || `#${sid}`}
          </Link>
        ) : (
          label
        ),
      },
    ];
  }
  return [
    {
      label: `Services (${services.length})`,
      full: true,
      value: (
        <div style={{ display: 'grid', gap: 'var(--vn-space-1)', minWidth: 0 }}>
          {services.map((svc, i) => (
            <Link
              key={`${svc.service_id}-${i}`}
              to={`/services/${svc.service_id}`}
              style={{ color: 'var(--vn-primary-text)', overflowWrap: 'anywhere' }}
            >
              {[svc.service_id, svc.service_name].filter(Boolean).join(' - ')}
            </Link>
          ))}
        </div>
      ),
    },
  ];
}

/**
 * Contract facts card — owner/service/dates/value/flags/partner over the shared
 * InfoGrid, mirroring the legacy `contract-info-card` cells. The status/role/
 * direction pills live in the page header; here we surface the record facts.
 */
export function ContractInfoCard({ detail }: { detail: ContractDetail }) {
  const items: InfoItem[] = [
    { label: 'Contract No', value: detail.contract_number_id },
    { label: 'Contract Key', value: detail.contract_key },
    { label: 'Owner', value: ownerValue(detail) },
    { label: 'Employee', value: detail.employee_name },
    { label: 'Product Group', value: detail.product_group_name },
    { label: 'Value', value: formatMoney(detail.contract_value) },
    { label: 'Effective Date', value: formatDate(detail.contract_effective_date) },
    { label: 'Expiry Date', value: formatDate(detail.contract_expiry_date) },
    { label: 'Registration Date', value: formatDate(detail.registration_date) },
    { label: 'Auto Renewable', value: boolChip(detail.auto_renewable) },
    { label: 'SLA', value: boolChip(detail.sla_enable) },
    { label: 'NDA', value: boolChip(detail.nda_enable) },
    ...servicesItems(detail),
  ];

  if (detail.partner_id) {
    items.push(
      {
        label: 'Partner',
        value: (
          <Link to={`/suppliers/${detail.partner_id}`} style={{ color: 'var(--vn-primary-text)' }}>
            {detail.partner_name || `#${detail.partner_id}`}
          </Link>
        ),
      },
      { label: 'Partner Code', value: detail.partner_code },
      { label: 'Partner Email', value: detail.partner_email },
      { label: 'Partner Phone', value: detail.partner_phone },
    );
  }

  if (detail.contract_note) {
    items.push({ label: 'Note', value: detail.contract_note, full: true });
  }

  return (
    <Card flat>
      <CardHeader>
        <CardTitle>Contract Information</CardTitle>
      </CardHeader>
      <CardBody>
        <InfoGrid items={items} />
      </CardBody>
    </Card>
  );
}
